import { get, post, del } from 'aws-amplify/api';

/**
 * Custom sports events ("league keywords") admins add on the Limits page.
 *
 * These used to live in localStorage, which meant a keyword existed only in the
 * browser that created it: other admins never saw it, clearing site data lost
 * it, and the AI assistant — which runs in Lambda — could neither read one nor
 * create one. They are now stored server side, so the page, every admin and the
 * assistant share one catalog.
 *
 * The built-in leagues are NOT served by this API; they ship in the page's
 * SPORTS_EVENTS list and are merged with these at render time.
 */

const API_NAME = 'coreuser';
const BASE_PATH = '/core/limits/catalog/sports-events';

/** @returns {Promise<Array>} the shared custom events, or [] when unreachable */
export const fetchCustomSportsEvents = async () => {
  try {
    const response = await get({ apiName: API_NAME, path: BASE_PATH }).response;
    const data = await response.body.json();
    return Array.isArray(data.events) ? data.events : [];
  } catch (error) {
    console.error('Error fetching the sports catalog:', error);
    return [];
  }
};

/**
 * Add one event.
 * @param {{name: string, category: string, icon?: string, aliases?: string[]}} event
 * @returns {Promise<{success: boolean, events?: Array, error?: string}>}
 */
export const createCustomSportsEvent = async (event) => {
  try {
    const response = await post({
      apiName: API_NAME,
      path: BASE_PATH,
      options: { body: event }
    }).response;

    const data = await response.body.json();
    return { success: true, events: data.events || [], added: data.added || [] };
  } catch (error) {
    console.error('Error creating a sports event:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Push a batch up in one request. Used once, to rescue the keywords that were
 * stranded in this browser's localStorage before the catalog was shared.
 */
export const importCustomSportsEvents = async (events) => {
  try {
    const response = await post({
      apiName: API_NAME,
      path: BASE_PATH,
      options: { body: { events } }
    }).response;

    const data = await response.body.json();
    return { success: true, events: data.events || [] };
  } catch (error) {
    console.error('Error importing sports events:', error);
    return { success: false, error: error.message };
  }
};

/** Remove one event. Limits already set against it are left alone. */
export const deleteCustomSportsEvent = async (eventId) => {
  try {
    const response = await del({
      apiName: API_NAME,
      path: `${BASE_PATH}/${encodeURIComponent(eventId)}`
    }).response;

    const data = await response.body.json();
    return { success: true, events: data.events || [] };
  } catch (error) {
    console.error('Error deleting a sports event:', error);
    return { success: false, error: error.message };
  }
};
